import type { TextAnnotation } from "@msuite/utils/types";

/** Shown in place of an empty label, so a fresh text annotation is still visible and clickable. */
export const TEXT_ANNOTATION_PLACEHOLDER = "Text";

export interface TextAnnotationBox {
	/** The label split into rendered lines. */
	lines: string[];
	/** Font size in image units. */
	fontSize: number;
	/** Distance between baselines in image units. */
	lineHeight: number;
	/** Inner padding of the panel in image units. */
	paddingX: number;
	paddingY: number;
	/** Top-left corner of the panel in image units. */
	x: number;
	y: number;
	/** Panel size in image units. */
	width: number;
	height: number;
}

/**
 * Resolve the panel a text annotation draws itself into: its lines, its type metrics and the
 * rectangle behind them, all in image units.
 *
 * The width is estimated from the longest line's character count rather than measured, which is
 * why the panel is comfortably padded. Both the SVG renderer and the in-place editor read this
 * one function, so the textarea always lands exactly where the drawn text was.
 *
 * @param annotation - The text annotation to measure.
 * @param baseScale - Rendered width divided by intrinsic image width.
 * @param placeholder - Text to measure when the label is empty.
 * @returns The panel geometry and the type metrics used to fill it.
 */
export function getTextAnnotationBox(
	annotation: TextAnnotation,
	baseScale: number,
	placeholder: string = TEXT_ANNOTATION_PLACEHOLDER,
): TextAnnotationBox {
	const lines = (annotation.text || placeholder).split("\n");
	const fontSize = (annotation.fontSize || 16) / baseScale;
	const lineHeight = fontSize * 1.45;
	const paddingX = 10 / baseScale;
	const paddingY = 7 / baseScale;
	const longest = Math.max(...lines.map((line) => line.length));

	return {
		lines,
		fontSize,
		lineHeight,
		paddingX,
		paddingY,
		x: annotation.x - paddingX / 4,
		y: annotation.y + paddingY / 2,
		width: longest * fontSize * 0.58 + paddingX * 2,
		height: lines.length * lineHeight + paddingY * 2,
	};
}
